import { Injector } from '@angular/core';
import { clamp, random } from '@sama/micro-dash';
import { ShapeLocationState2D } from '../../../cross-team-shared/annotation/shared/state/shape-location-state';
import {
  InteractiveBorderEventInfo,
  SubpointEventInfo,
} from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { Dimensions } from '../../../cross-team-shared/types/misc';
import { VectorLayerEditMode } from '../../../cross-team-shared/types/tool-modes';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { SliderTranslationHelper } from '../shapes/slider-rectangle/slider-translation-helper';
import { VectorDragHandler } from '../tools/shared/drag-handlers/vector-drag-handler';
import { RectangleHelper } from './rectangle-helper';

export const SLIDER_INDEX = 4;

export class SliderRectangleHelper extends RectangleHelper {
  override allowsMLExtrapolation = false;

  override arePointsValid(points: Point2D[]) {
    return super.arePointsValid(points.slice(0, 4));
  }

  override getSingleSubpointDragHandler(
    eventInfo: SubpointEventInfo,
    editMode: VectorLayerEditMode,
    injector: Injector,
  ) {
    if (eventInfo.subpointIndex === SLIDER_INDEX) {
      return new VectorDragHandler(
        eventInfo.event,
        injector,
        new SliderTranslationHelper(injector, eventInfo.shapeStore),
        {
          restrictInteractions: true,
        },
      );
    } else {
      return super.getSingleSubpointDragHandler(eventInfo, editMode, injector);
    }
  }

  override getInteractiveBorderDragHandler(
    eventInfo: InteractiveBorderEventInfo,
    injector: Injector,
  ) {
    if (eventInfo.lineIndex === SLIDER_INDEX) {
      const subpointEvenInfo = {
        ...eventInfo,
        target: 'subpoint' as const,
        subpointIndex: SLIDER_INDEX,
      };
      return new VectorDragHandler(
        subpointEvenInfo.event,
        injector,
        new SliderTranslationHelper(injector, subpointEvenInfo.shapeStore),
        {
          restrictInteractions: true,
        },
      );
    } else {
      return super.getInteractiveBorderDragHandler(eventInfo, injector);
    }
  }

  override getCursorForInteractiveBorder(editMode: string, lineIndex: number) {
    if (editMode === 'drag' && lineIndex === SLIDER_INDEX) {
      return 'ew-resize';
    }
    return super.getCursorForInteractiveBorder(editMode, lineIndex);
  }

  override getMinPoints() {
    return 5;
  }

  override makeRandomPoints(imageSize: Dimensions) {
    const points = super.makeRandomPoints(imageSize);
    switch (random(4)) {
      case 0:
        points.push(points[0]);
        break;
      case 1:
        points.push(points[1]);
        break;
      default:
        points.push([random(points[0][0], points[1][0]), points[0][1]]);
    }
    return points;
  }

  override locationsAnswerToState(locations: ShapeLocationState2D[]) {
    const fixedLocations = locations.map((location) => {
      const rectanglePoints = location.points.slice(0, 4);

      // Force slider point Y coordinate to the top edge of the rectangle to
      // prevent issues with dragging and dimension text position
      const fixedSliderPoint: Point2D = [
        getSliderX(location.points),
        location.points[0][1],
      ];
      return { ...location, points: [...rectanglePoints, fixedSliderPoint] };
    });

    return super.locationsAnswerToState(fixedLocations);
  }

  override fixPoints(
    points: Point2D[],
    initialPointsForSliderLocking?: Point2D[],
  ) {
    const fixed = super.fixPoints(points, initialPointsForSliderLocking);

    if (points.length === 5) {
      fixed.push([
        fixSliderX(getSliderX(points), fixed, initialPointsForSliderLocking),
        fixed[0][1],
      ]);
    }

    return fixed;
  }
}

export function isSliderLockedLeft(points: Point2D[]) {
  return getSliderX(points) === getLeft(points);
}

export function isSliderLockedRight(points: Point2D[]) {
  return getSliderX(points) === getRight(points);
}

export function getSliderX(points: Point2D[]) {
  return points[SLIDER_INDEX][0];
}

function fixSliderX(
  sliderX: number,
  points: Point2D[],
  startingPoints?: Point2D[],
) {
  if (startingPoints) {
    if (isSliderLockedLeft(startingPoints)) {
      return getLeft(points);
    } else if (isSliderLockedRight(startingPoints)) {
      return getRight(points);
    }
  }
  return clamp(sliderX, getLeft(points), getRight(points));
}

function getLeft(points: Point2D[]) {
  return points[0][0];
}

function getRight(points: Point2D[]) {
  return points[1][0];
}
